import { useState } from "react";

import { Link, useLocation } from "react-router-dom";

import { ChevronDown, ChevronRight } from "lucide-react";

import { roleMenuItems } from "~/components/layout/utils/menuItems";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  useSidebar,
} from "~/components/ui/sidebar";
import { useUnreadContactAttempts } from "~/hooks/useUnreadContactAttempts";
import { UserRole } from "~/types/user";

interface SidebarNavigationProps {
  userRole: UserRole;
}

export function SidebarNavigation({ userRole }: SidebarNavigationProps) {
  const location = useLocation();
  const menuItems = roleMenuItems[userRole] || [];
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
  const { unreadCount } = useUnreadContactAttempts();
  const { isMobile, setOpenMobile } = useSidebar();

  const closeMobileSidebar = () => {
    if (isMobile) setOpenMobile(false);
  };

  const toggleSubmenu = (title: string) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  // Check if any submenu item is active
  const isSubmenuActive = (item: (typeof menuItems)[0]) => {
    if (!item.submenu) return false;
    return item.submenu.some((subItem) => location.pathname === subItem.url);
  };

  return (
    <SidebarMenu>
      {menuItems.map((item, index) => {
        const hasSubmenu = item.submenu && item.submenu.length > 0;
        const isActive =
          location.pathname === item.url || isSubmenuActive(item);
        const isOpen = openSubmenus[item.title] || isSubmenuActive(item);
        const IconComponent = item.icon;

        return (
          <SidebarMenuItem key={index}>
            {hasSubmenu ? (
              <>
                <SidebarMenuButton
                  isActive={isActive}
                  onClick={() => toggleSubmenu(item.title)}
                  tooltip={item.title}
                >
                  <IconComponent size={16} />
                  <span>{item.title}</span>
                  {isOpen ? (
                    <ChevronDown size={16} className="ml-auto" />
                  ) : (
                    <ChevronRight size={16} className="ml-auto" />
                  )}
                </SidebarMenuButton>

                {isOpen && item.submenu && (
                  <SidebarMenuSub>
                    {item.submenu.map((subItem, subIndex) => {
                      if (subItem.isSection) {
                        return (
                          <li
                            key={`${index}-${subIndex}`}
                            className="px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground first:pt-1"
                          >
                            {subItem.title}
                          </li>
                        );
                      }

                      const isSubActive = location.pathname === subItem.url;
                      const SubIconComponent = subItem.icon;

                      return (
                        <SidebarMenuItem key={`${index}-${subIndex}`}>
                          <SidebarMenuSubButton asChild isActive={isSubActive}>
                            <Link to={subItem.url} onClick={closeMobileSidebar}>
                              <SubIconComponent size={14} />
                              <span>{subItem.title}</span>
                              {subItem.title === "Cotizaciones" &&
                                unreadCount > 0 && (
                                  <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-orange-500 text-xs font-semibold text-white">
                                    {unreadCount}
                                  </span>
                                )}
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenuSub>
                )}
              </>
            ) : (
              <SidebarMenuButton
                isActive={isActive}
                asChild
                tooltip={item.title}
              >
                <Link to={item.url} onClick={closeMobileSidebar}>
                  <IconComponent size={16} />
                  <span>{item.title}</span>
                </Link>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}
